import { Suspense, useContext, useEffect } from 'react'
import { Redirect, Route, RouteProps } from 'react-router-dom'
import {
  IonApp,
  IonRouterOutlet,
  isPlatform,
  setupIonicReact,
} from '@ionic/react'
import { IonReactRouter } from '@ionic/react-router'
import { App as CapacitorApp } from '@capacitor/app'
import { StatusBar, Style } from '@capacitor/status-bar'
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook'
import { useIsRestoring } from '@tanstack/react-query'

import { Activity } from '@components/pages/Activity'
import { ContestDetailsListPage } from '@components/pages/Contest/ContestDetailsListPage'
import { ContestDetailsMobileWebListPage } from '@components/pages/Contest/ContestDetailsMobileWebListPage'
import { ContestDetailsPage } from '@components/pages/Contest/ContestDetailsPage'
import { Leaderboard } from '@components/pages/Contest/Leaderboard'
import { ContestEditor } from '@components/pages/ContestEditor'
import { CustomRewards } from '@components/pages/CustomRewards'
import { EmailValidation } from '@components/pages/EmailValidation'
import { Feed } from '@components/pages/Feed'
import { FindUsers } from '@components/pages/FindUsers'
import { HomeOrLogin } from '@components/pages/HomeOrLogin'
import LoadingPage from '@components/pages/LoadingPage'
import { NotFound } from '@components/pages/NotFound'
import {
  Remove2FA,
  SecuritySettings,
  Setup2FA,
} from '@components/pages/SecuritySettings'
import { UserProfile } from '@components/pages/UserProfile'
import { UserRelations } from '@components/pages/UserRelations'
import { DiscoverFeed } from '@/components/pages/DiscoverFeed'
import {
  RedirectToLogin,
  RedirectToLogout,
} from '@/components/pages/RedirectToAuth'
import { CommentsPage } from '@/components/pages/UGC/Comments'
import { ConversationManagement } from '@/components/pages/UGC/Conversation'
import { ConversationComposer } from '@/components/pages/UGC/Conversation/ConversationComposer'
import { ConversationReplyComposer } from '@/components/pages/UGC/Conversation/ConversationReplyComposer'
import { Channels } from '@/components/pages/UGC/Messaging/Channels'
import { Messages } from '@/components/pages/UGC/Messaging/Messages'
import { PostComposer } from '@/components/pages/UGC/PostComposer/PostComposer'
import { EditProfile } from '@/components/pages/UGC/Profile/EditProfile'
import { WalletPageContainer } from '@/components/pages/WalletPageContainer'

import { appConfig } from '@services/config'
import '@/services/awsAmplify/config/amplify'
import { recordPageView, recordStartSession } from '@/services/awsAmplify'
import { pushNotificationsInit } from '@/services/pushNotifications'
import { connectSsoIntegration } from '@/services/sso'
import { webPushNotificationsInit } from '@/services/webPushNotifications'
import { useServiceWorkerUpdateContent } from '@/lib/hooks/useServiceWorkerUpdateContent'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { PromptToInstallProvider } from '@/lib/promptToInstallProvider'
import { Context } from '@/lib/store'
import { ActionType } from '@/lib/store/reducer'
import { Tracking, TrackingEvent } from '@/lib/tracking'
import { Routes } from '@/router/routes'
import { RoutingInterceptor } from '@/router/RoutingInterceptor'
import { securityGuard } from '@/router/securityGuard'
import { isIosWebView, isNative } from '@/utils/utils'

import { Pages } from '@/enums'

import { OpenAppReward } from '../organisms/OpenAppReward'
import { Confirm } from '../pages/Auth/Confirm'
import { Consent } from '../pages/Auth/Consent'
import { LogInOptions, SignUpOptions } from '../pages/Auth/SignInOptions'
import Email from '../pages/Auth/SignInOptions/Email/Email'
import PhoneNumber from '../pages/Auth/SignInOptions/SMS/PhoneNumber'
import { Deposit } from '../pages/Deposit'
import {
  LivenessCheck,
  LivenessFailed,
  LivenessInstructions,
} from '../pages/FaceLiveness'
import { InterestsSurvey } from '../pages/InterestsSurvey'
import { PwaOnboarding } from '../pages/PwaOnboarding'
import { Stake } from '../pages/Stake'
import { StakeVault } from '../pages/StakeVault'
import { LikesPage } from '../pages/UGC/Likes/Likes'
import CreateProfile from '../pages/UGC/Profile/CreateProfile'
import { Withdraw } from '../pages/Withdraw'
import { authGuard } from './authGuard'
import { updateApp } from './updateApp'

import '@styles/global.scss'

setupIonicReact({
  swipeBackEnabled: !isIosWebView(),
  innerHTMLTemplatesEnabled: true,
})

connectSsoIntegration()

function toggleDarkTheme(shouldAdd) {
  document.body?.classList.toggle('dark', shouldAdd)
}
toggleDarkTheme(true)

updateApp()

if (isNative()) {
  window.addEventListener('load', async () => {
    pushNotificationsInit()
    try {
      await StatusBar.setStyle({
        style: Style.Dark,
      })
      await StatusBar.setBackgroundColor({ color: '#131820' })
    } catch (e: unknown) {
      console.log(e)
    }
  })
} else {
  webPushNotificationsInit()
}
/* 
 Listen for when the app or activity are resumed.
 ionic: https://capacitorjs.com/docs/apis/app#addlistenerresume-
*/
CapacitorApp.addListener('resume', () => {
  Tracking.triggerEvent(TrackingEvent.AppOpened)
  recordStartSession()
  recordPageView()
  const pathname = window.location.pathname
  pathname === `/${Pages.Feed}` && Tracking.triggerEvent(TrackingEvent.Feed)
})

export const App = () => {
  useServiceWorkerUpdateContent()

  // When the user exits the APP and re-enters the APP
  const { dispatch } = useContext(Context)

  useEffect(() => {
    const ugcComposerHandler = (e) => {
      console.log('ugcComposerHandler', e?.detail?.isOpen)
      dispatch({
        type: ActionType.SET_VIDEO_PLAYBACK_ALLOWED,
        payload: !e?.detail?.isOpen,
      })
    }

    Tracking.triggerEvent(TrackingEvent.AppOpened)
    if (isNative()) {
      window.addEventListener('ugcComposer', ugcComposerHandler)
    }
    return () => {
      if (isNative()) {
        window.removeEventListener('ugcComposer', ugcComposerHandler)
      }
    }
  }, [dispatch])

  const gtmArgs = {
    id: appConfig.gtmId,
  }

  const { isFetched, data: userInfo } = useUserInfo()
  const isRestoring = useIsRestoring()

  const isAuthenticated = !!userInfo && !!userInfo?.username

  if (!isFetched || isRestoring) {
    return <LoadingPage />
  }

  const protectedRoute = (callback) => {
    authGuard()

    if (isAuthenticated) {
      return callback
    }

    if (window.location.pathname !== Routes.AUTH_SIGN_UP) {
      dispatch({
        type: ActionType.SIGN_IN_REDIRECT,
        payload: window.location.pathname + window.location.search,
      })
    }

    return <Redirect to={Routes.AUTH_SIGN_UP} />
  }

  const publicRoute = (callback) => {
    if (!userInfo?.username || userInfo.deactivated) {
      return callback
    }
    return <Redirect to={Routes.LOGIN} />
  }

  const routes: RouteProps[] = [
    {
      exact: true,
      path: Routes.ROOT,
      component: HomeOrLogin,
    },
    {
      path: Routes.LOGIN,
      component: RedirectToLogin,
    },
    {
      path: Routes.LOGOUT,
      component: RedirectToLogout,
    },
    {
      path: Routes.ACTIVITY,
      render: (props) => protectedRoute(<Activity {...props} />),
    },
    {
      exact: true,
      path: Routes.AUTH_CONFIRM,
      render: (props) => publicRoute(<Confirm {...props} />),
    },
    {
      exact: true,
      path: Routes.AUTH_CONSENT,
      render: (props) => publicRoute(<Consent {...props} />),
    },
    {
      exact: true,
      path: Routes.AUTH_CREATE_PROFILE,
      render: (props) => publicRoute(<CreateProfile {...props} />),
    },
    {
      exact: true,
      path: Routes.AUTH_CREATE_INTERESTS_SURVEY,
      render: (props) => protectedRoute(<InterestsSurvey {...props} />),
    },
    {
      exact: true,
      path: Routes.PWA_ONBOARDING,
      render: (props) => protectedRoute(<PwaOnboarding {...props} />),
    },
    {
      exact: true,
      path: Routes.AUTH_EMAIL,
      component: Email,
    },
    {
      exact: true,
      path: Routes.AUTH_LOGIN,
      render: (props) => publicRoute(<LogInOptions {...props} />),
    },
    {
      exact: true,
      path: Routes.AUTH_PHONE,
      render: (props) => publicRoute(<PhoneNumber {...props} />),
    },
    {
      exact: true,
      path: Routes.AUTH_SIGN_UP,
      render: (props) => publicRoute(<SignUpOptions {...props} />),
    },
    {
      path: Routes.COMMENTS,
      render: (props) => protectedRoute(<CommentsPage {...props} />),
    },
    {
      exact: true,
      path: Routes.CONTEST_DETAIL,
      render: (props) => protectedRoute(<ContestDetailsPage {...props} />),
    },
    {
      path: Routes.CONTEST_DETAIL_VIDEOS,
      render: (props) => {
        return protectedRoute(
          isPlatform('mobileweb') ? (
            <ContestDetailsMobileWebListPage {...props} />
          ) : (
            <ContestDetailsListPage {...props} />
          )
        )
      },
    },
    {
      path: Routes.CONTEST_EDITOR,
      render: (props) => protectedRoute(<ContestEditor {...props} />),
    },
    {
      exact: true,
      path: Routes.CONTEST_LEADERBOARD,
      render: (props) => protectedRoute(<Leaderboard {...props} />),
    },
    {
      exact: true,
      path: Routes.CONVERSATION_CREATE,
      render: (props) => protectedRoute(<ConversationComposer {...props} />),
    },
    {
      exact: true,
      path: Routes.CONVERSATION_MANAGE,
      render: (props) => protectedRoute(<ConversationManagement {...props} />),
    },
    {
      exact: true,
      path: Routes.CONVERSATION_REPLY,
      render: (props) =>
        protectedRoute(<ConversationReplyComposer {...props} />),
    },
    {
      path: Routes.CUSTOM_REWARDS,
      render: (props) => protectedRoute(<CustomRewards {...props} />),
    },
    {
      path: Routes.DISCOVER,
      render: (props) => protectedRoute(<DiscoverFeed {...props} />),
    },
    {
      path: Routes.FEED,
      render: (props) => protectedRoute(<Feed {...props} />),
    },
    {
      path: Routes.LIKES,
      render: (props) => protectedRoute(<LikesPage {...props} />),
    },
    {
      exact: true,
      path: Routes.MESSAGES,
      render: (props) => protectedRoute(<Channels {...props} />),
    },
    {
      path: Routes.MESSAGES_USER,
      render: (props) => protectedRoute(<Messages {...props} />),
    },
    {
      path: Routes.MESSAGES_FIND_USERS,
      render: () =>
        protectedRoute(
          <FindUsers linkToMessages={true} title="messaging.newChat" />
        ),
    },
    {
      exact: true,
      path: Routes.POST,
      render: (props) => protectedRoute(<PostComposer {...props} />),
    },
    {
      exact: true,
      path: Routes.PROFILE,
      render: (props) => protectedRoute(<UserProfile {...props} />),
    },
    {
      path: Routes.PROFILE_EDIT,
      render: (props) => protectedRoute(<EditProfile {...props} />),
    },
    {
      path: Routes.SECURITY_SETTINGS,
      render: () => protectedRoute(<SecuritySettings />),
    },
    {
      path: Routes.SETUP_2FA,
      render: () => protectedRoute(<Setup2FA />),
    },
    {
      path: Routes.REMOVE_2FA,
      render: () => protectedRoute(<Remove2FA />),
    },
    {
      path: Routes.PROFILE_USER,
      render: (props) => protectedRoute(<UserProfile {...props} />),
    },
    {
      path: Routes.RELATIONS_USER,
      render: (props) => protectedRoute(<UserRelations {...props} />),
    },
    {
      path: Routes.VALIDATE_EMAIL,
      render: () => <EmailValidation />,
    },
    {
      path: Routes.VAULT,
      render: (props) => protectedRoute(<WalletPageContainer {...props} />),
    },
    {
      path: Routes.WITHDRAW,
      render: () =>
        protectedRoute(securityGuard(userInfo, <Withdraw />, Routes.WITHDRAW)),
    },
    {
      path: Routes.DEPOSIT,
      render: () =>
        protectedRoute(securityGuard(userInfo, <Deposit />, Routes.DEPOSIT)),
    },
    {
      path: Routes.STAKE,
      render: () => protectedRoute(<Stake />),
    },
    {
      path: Routes.STAKE_VAULT,
      render: () => protectedRoute(<StakeVault />),
    },
    {
      path: Routes.FACE_LIVENESS_INSTRUCTIONS,
      render: () => protectedRoute(<LivenessInstructions />),
    },
    {
      path: Routes.FACE_LIVENESS_CHECK,
      render: () => protectedRoute(<LivenessCheck />),
    },
    {
      path: Routes.FACE_LIVENESS_FAILED,
      render: () => protectedRoute(<LivenessFailed />),
    },
  ]

  return (
    <GTMProvider state={gtmArgs}>
      <PromptToInstallProvider>
        <IonApp>
          <IonReactRouter>
            <Suspense fallback={<LoadingPage />}>
              <RoutingInterceptor>
                <OpenAppReward>
                  <IonRouterOutlet animated={!isIosWebView()} id="main">
                    {routes.map((props: RouteProps) => (
                      <Route key={`${props.path}`} {...props} />
                    ))}
                    <Route component={NotFound} />
                  </IonRouterOutlet>
                </OpenAppReward>
              </RoutingInterceptor>
            </Suspense>
          </IonReactRouter>
        </IonApp>
      </PromptToInstallProvider>
    </GTMProvider>
  )
}
